import { ReactNode } from "react";

import i18n from "../../i18n";
import type { en } from "../../locales/en";

import { UserDto } from "../../DTOs/User/UserDto";
import { AreaKey } from "../../utilities/helpers/pageVocabulary.generated";
import { hasArea, hasTile } from "../../utilities/helpers/roleUtilities";
import { CardSize, PlacedCard } from "./dashboardLayout";
import { DrillBlastTodayCard } from "./DashboardCards/DrillBlastTodayCard";
import { ExplosivesStockCard } from "./DashboardCards/ExplosivesStockCard";
import { HourlyMetricsCard } from "./DashboardCards/HourlyMetricsCard";
import { ImportChecksCard } from "./DashboardCards/ImportChecksCard";
import { LazyMachinePositionsCard } from "./DashboardCards/LazyMachinePositionsCard";
import { MaterialMovementCard } from "./DashboardCards/MaterialMovementCard";
import { PlantStatusCard } from "./DashboardCards/PlantStatusCard";
import { TargetsCard } from "./DashboardCards/TargetsCard";

export type DeploymentFeatures = { telemetryEnabled: boolean; drillBlastModules: string[] };

export type DashboardCardKey = keyof typeof en.dashboard.cards;

export const cardTitle = (key: DashboardCardKey) => i18n.t(`cards.${key}.title`, { ns: "dashboard" });

export const cardLinkTooltip = (key: DashboardCardKey) => i18n.t(`cards.${key}.link`, { ns: "dashboard" });

export interface DashboardCardDefinition {
  key: DashboardCardKey;
  linkTo: string;
  linkArea: AreaKey;
  defaultSize: CardSize;
  isVisible?: (user: UserDto | null, features: DeploymentFeatures) => boolean;
  content: ReactNode;
}

export const DASHBOARD_CARDS: DashboardCardDefinition[] = [
  {
    key: "material-movement",
    linkTo: "/production/show",
    linkArea: "Production",
    defaultSize: { w: 6, h: "medium" },
    content: <MaterialMovementCard />,
  },
  {
    key: "plant-status",
    linkTo: "/stoppages",
    linkArea: "Stoppages",
    defaultSize: { w: 6, h: "medium" },
    content: <PlantStatusCard />,
  },
  {
    key: "targets",
    linkTo: "/admin/references/target-refs/targets",
    linkArea: "Targets",
    defaultSize: { w: 6, h: "medium" },
    content: <TargetsCard />,
  },
  {
    key: "import-checks",
    linkTo: "/operations",
    linkArea: "Operations",
    defaultSize: { w: 6, h: "medium" },
    content: <ImportChecksCard />,
  },
  {
    key: "machine-positions",
    linkTo: "/telemetry/machine-positions",
    linkArea: "MachinePositions",
    defaultSize: { w: 6, h: "medium" },
    isVisible: (user, { telemetryEnabled }) => telemetryEnabled && hasArea(user, "MachinePositions"),
    content: <LazyMachinePositionsCard />,
  },
  {
    key: "drill-blast-today",
    linkTo: "/db/bh-drilling-list",
    linkArea: "BlastHoleDrillingList",
    defaultSize: { w: 6, h: "short" },
    content: <DrillBlastTodayCard />,
  },
  {
    key: "explosives-stock",
    linkTo: "/db/explosives",
    linkArea: "ExplosivesStore",
    defaultSize: { w: 6, h: "short" },
    isVisible: (user, { drillBlastModules }) => drillBlastModules.includes("ExplosivesStore") && hasArea(user, "ExplosivesStore"),
    content: <ExplosivesStockCard />,
  },
  {
    key: "hourly-metrics",
    linkTo: "/reports/daily",
    linkArea: "DailyReport",
    defaultSize: { w: 12, h: "medium" },
    content: <HourlyMetricsCard />,
  },
];

export type DashboardPlacement = PlacedCard<DashboardCardDefinition>;

export const availableCards = (user: UserDto | null, features: DeploymentFeatures): DashboardCardDefinition[] =>
  DASHBOARD_CARDS.filter(card => hasTile(user, card.key) && (card.isVisible?.(user, features) ?? true));
